<template>

    <v-input-group v-if="inputType === 'password'">
        <input ref="inputEl"
            class="input"
            :class="classes"
            :type="type"
            :placeholder="placeholder"
            v-model="val" />
        <template #suffix>
            <div class="input-eye" v-if="inputType === 'password'" @click="togglePassword">
                <i class="fas" :class="type === 'password' ? 'fa-eye' : 'fa-eye-slash'" />
            </div>
        </template>
    </v-input-group>

    <div v-else-if="clearable" class="input-clearable">
        <input ref="inputEl"
            class="input"
            :class="[classes, { 'input--with-clear': showClearControl }]"
            :type="type"
            :placeholder="placeholder"
            v-model="val" />
        <button
            v-if="showClearControl"
            type="button"
            class="input__clear"
            :aria-label="clearAriaLabel || undefined"
            :disabled="clearDisabled"
            @click.prevent="onClearClick"
        >
            <i aria-hidden="true" class="fas fa-circle-xmark" />
        </button>
    </div>

    <input v-else ref="inputEl"
        class="input"
        :class="classes"
        :type="type"
        :placeholder="placeholder"
        v-model="val" />

</template>

<style scoped lang="scss">

    .input {
        @include font-regular(14px);
        appearance: none;
        border: 1px solid $color-rule;
        border-radius: 5px;
        box-sizing: border-box;
        display: block;
        flex-grow: 1;
        outline: none;
        transition: border-color .3s;
        width: 100%;
        z-index: 1;
        &:hover, &:focus {
            border-color: $color-text;
            color: $color-text;
        }
        &:disabled {
            background: $color-bg !important;
            border-color: $color-rule;
            &:hover {
                border-color: $color-rule;
            }
        }
    }

    .input {
        &.theme-light {
            background: $color-white;
            color: $color-text;
        }
        &.theme-trans {
            background: transparent;
            border: none;
            color: $color-white;
        }
    }

    .input {
        &.size-xs {
            font-size: 12px;
            height: 34px;
            padding: 0 10px;
        }
        &.size-sm {
            font-size: 13px;
            height: 40px;
            padding: 0 12px;
        }
        &.size-md {
            font-size: 14px;
            height: 50px;
            padding: 0 15px;
        }
        &.size-lg {
            font-size: 15px;
            height: 60px;
            padding: 0 20px;
        }
    }

    .input-clearable {
        min-width: 0;
        position: relative;
        width: 100%;
    }

    .input {
        &.input--with-clear {
            padding-right: 48px !important;
        }
    }

    .input__clear {
        align-items: center;
        background: transparent;
        border: 0;
        border-radius: 5px;
        color: $color-muted;
        cursor: pointer;
        display: flex;
        justify-content: center;
        padding: 0 2px 0 8px;
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        transition: color 0.2s, opacity 0.2s;
        &:hover:not(:disabled) {
            color: $color-text;
        }
        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
        .fas {
            font-size: 16px;
        }
    }

    .input-eye {
        cursor: pointer;
        transition: .3s opacity;
        width: 20px;
        &:hover {
            opacity: 0.7;
        }
    }

</style>

<script>

export default {

    props: {
        clearAriaLabel: {
            type: String,
            default: null,
        },
        clearDisabled: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        model: {
            required: false,
        },
        size: {
            type: String,
            default: 'md',
        },
        theme: {
            type: String,
            default: 'light',
        },
        type: {
            type: String,
            default: 'text',
        },
        placeholder: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            val: this.model,
            inputType: this.type,
        }
    },

    watch: {

        model(val) {
            this.val = val;
        },

        val(val) {
            this.$emit('input', val);
        },

    },

    computed: {

        classes() {
            return {
                'theme-light': this.theme === 'light',
                'theme-trans': this.theme === 'trans',
                'size-xs': this.size === 'xs',
                'size-sm': this.size === 'sm',
                'size-md': this.size === 'md',
                'size-lg': this.size === 'lg',
            };
        },

        showClearControl() {
            if (!this.clearable) {
                return false;
            }
            const v = this.val;
            if (v === null || v === undefined) {
                return false;
            }
            return String(v).trim() !== '';
        },

    },

    methods: {

        focus() {
            this.$nextTick(() => {
                const el = this.$refs.inputEl;
                if (el && typeof el.focus === 'function') {
                    el.focus();
                }
            });
        },

        onClearClick() {
            this.val = '';
            this.$emit('clear');
        },

        togglePassword() {
            if (this.type === 'password') {
                this.type = 'text';
            } else {
                this.type = 'password';
            }
        },

    },

}

</script>
